/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2018 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { canUseDOM, getCSSStyleDeclaration } from '@instructure/ui-dom-utils';
let defaultDir;
let dirAttribute;
let observer;
const getDefaultDir = () => {
  /**
   * use a cached value for the default of <html> element's "dir" so we don't
   * have to call the expensive getCSSStyleDeclaration to look it it up every time
   */
  if (defaultDir) {
    return defaultDir;
  }
  if (canUseDOM) {
    var _getCSSStyleDeclarati;
    const htmlEl = document.documentElement;
    dirAttribute = htmlEl.getAttribute('dir');
    // https://developer.mozilla.org/en-US/docs/Web/CSS/direction
    defaultDir = dirAttribute || ((_getCSSStyleDeclarati = getCSSStyleDeclaration(htmlEl)) === null || _getCSSStyleDeclarati === void 0 ? void 0 : _getCSSStyleDeclarati.direction);
    if (!observer) {
      observer = new MutationObserver(() => {
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/dir
        const attr = htmlEl.getAttribute('dir');
        if (attr && attr !== dirAttribute) {
          dirAttribute = defaultDir = attr;
        }
      });
      observer.observe(htmlEl, {
        attributes: true
      });
    }
    return defaultDir;
  }
  return void 0;
};
/**
 * ---
 * category: utilities/i18n
 * ---
 *
 * Return the direction ('ltr' or 'rtl' or 'auto') of an element. If no element
 * is given, it returns the document's 'dir' value.
 * @module getTextDirection
 * @param {Element} element will use the <html> element by default
 * @returns {String} 'ltr' or 'rtl' or 'auto' (or `undefined` if no DOM is present)
 */
function getTextDirection(element) {
  if (canUseDOM) {
    var _getCSSStyleDeclarati2;
    if (typeof element === 'undefined' || element === document.documentElement) return getDefaultDir();
    return element.getAttribute('dir') || ((_getCSSStyleDeclarati2 = getCSSStyleDeclaration(element)) === null || _getCSSStyleDeclarati2 === void 0 ? void 0 : _getCSSStyleDeclarati2.direction);
  }
  return void 0;
}
export default getTextDirection;
export { getTextDirection };